import { useState, useEffect } from 'react';
import {
  TextInput,
  Stack,
  Text,
  Loader,
  Center,
  UnstyledButton,
  Group,
  Badge,
  ActionIcon,
  Button,
} from '@mantine/core';
import { IconSearch, IconX, IconMapPin, IconCheck } from '@tabler/icons-react';
import { useDebounce } from '@ayocore/platform/client/hooks/useDebounce';
import { useCloseModal } from '@ayocore/platform/client/components/ModalHost/ModalContext';
import { searchCompany } from '@ayocore/erp/crm/client/rpc';
import type { CompanyInfo } from '@ayocore/erp/crm/common/types/company';
import logger from '@ayocore/platform/common/helpers/logger';

export interface ExternalCompanySearchViewProps extends Record<string, unknown> {
  onSelect: (company: CompanyInfo) => void;
  initialQuery?: string;
}

function formatCity(city: string): string {
  return city.replace(/^г\.?\s+/i, '').trim();
}

function CompanyCard({
  company,
  selected,
  onSelect,
}: {
  company: CompanyInfo;
  selected: boolean;
  onSelect: (c: CompanyInfo) => void;
}) {
  return (
    <UnstyledButton
      onClick={() => onSelect(company)}
      style={{
        width: '100%',
        padding: '8px 10px',
        borderRadius: 6,
        border: selected
          ? '1.5px solid var(--mantine-color-blue-filled)'
          : '1px solid var(--mantine-color-gray-2)',
        backgroundColor: selected ? 'var(--mantine-color-blue-0)' : '#fff',
        cursor: 'pointer',
        overflow: 'hidden',
        transition: 'border-color 0.12s ease, background-color 0.12s ease',
      }}
    >
      <Group justify='space-between' wrap='nowrap' align='center'>
        <Stack gap={2} style={{ flex: 1, minWidth: 0 }}>
          <Group gap='xs' wrap='nowrap' justify='space-between'>
            <Group gap='xs' wrap='nowrap' style={{ minWidth: 0 }}>
              <Text size='sm' fw={500} style={{ minWidth: 0 }} truncate>
                {company.name}
              </Text>
              {company.type === 'individual' && (
                <Badge size='xs' variant='light' color='orange' style={{ flexShrink: 0 }}>
                  ИП
                </Badge>
              )}
            </Group>
            {company.city && (
              <Group gap={2} wrap='nowrap' style={{ flexShrink: 0 }}>
                <IconMapPin size={11} color='var(--mantine-color-violet-6)' />
                <Text size='xs' c='violet'>
                  {formatCity(company.city)}
                </Text>
              </Group>
            )}
          </Group>
          <Group gap='xs'>
            {company.inn && (
              <Text size='xs' c='dimmed'>
                ИНН: {company.inn}
              </Text>
            )}
            {company.ogrn && (
              <Text size='xs' c='dimmed'>
                ОГРН: {company.ogrn}
              </Text>
            )}
          </Group>
          {company.address && (
            <Text size='xs' c='dimmed' lineClamp={1}>
              {company.address}
            </Text>
          )}
        </Stack>
        {selected && (
          <IconCheck size={18} color='var(--mantine-color-blue-filled)' style={{ flexShrink: 0 }} />
        )}
      </Group>
    </UnstyledButton>
  );
}

/**
 * Содержимое модалки поиска компании по внешнему источнику (DaData / ЕГРЮЛ).
 *
 * Используется через useModal:
 * @example
 * const searchModal = useModal(ExternalCompanySearchView);
 * searchModal.open({
 *   title: 'Найти компанию',
 *   onSelect: (c) => { fill(c); searchModal.close(); },
 * });
 */
export function ExternalCompanySearchView({
  onSelect,
  initialQuery = '',
}: ExternalCompanySearchViewProps) {
  const { close } = useCloseModal();
  const [query, setQuery] = useState(initialQuery);
  const [results, setResults] = useState<CompanyInfo[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<CompanyInfo | null>(null);

  const debouncedQuery = useDebounce(query, 350);

  useEffect(() => {
    if (debouncedQuery.length < 2) {
      setResults([]);
      setSelected(null);
      return;
    }
    const run = async () => {
      setLoading(true);
      try {
        const found = await searchCompany(debouncedQuery);
        setResults(Array.isArray(found) ? found : []);
        setSelected(null);
      } catch (err) {
        logger.error('[ExternalCompanySearchModal] Ошибка поиска:', err);
        setResults([]);
      } finally {
        setLoading(false);
      }
    };
    void run();
  }, [debouncedQuery]);

  const handleAdd = () => {
    if (selected) {
      onSelect(selected);
    }
  };

  return (
    <Stack gap='sm' style={{ width: '100%', minWidth: 0, overflow: 'hidden' }}>
      <TextInput
        placeholder='Введите ИНН или название...'
        leftSection={<IconSearch size={14} />}
        rightSection={
          query.length > 0 ? (
            <ActionIcon
              variant='subtle'
              color='gray'
              size='sm'
              onClick={() => {
                setQuery('');
                setResults([]);
                setSelected(null);
              }}
            >
              <IconX size={14} />
            </ActionIcon>
          ) : null
        }
        rightSectionPointerEvents={query.length > 0 ? 'all' : 'none'}
        value={query}
        onChange={e => setQuery(e.currentTarget.value)}
        autoFocus
        size='sm'
      />

      {loading && (
        <Center py='md'>
          <Loader size='sm' />
        </Center>
      )}

      {!loading && query.length < 2 && (
        <Text size='xs' c='dimmed' ta='center' py='md'>
          Введите ИНН или название компании для поиска по базе ЕГРЮЛ (ЕГРИП)
        </Text>
      )}

      {!loading && query.length >= 2 && results.length === 0 && (
        <Text size='xs' c='dimmed' ta='center' py='md'>
          Ничего не найдено
        </Text>
      )}

      {!loading && results.length > 0 && (
        <div style={{ maxHeight: 420, overflowY: 'auto', width: '100%', minWidth: 0 }}>
          <Stack gap={4}>
            {results.map((company, i) => (
              <CompanyCard
                key={i}
                company={company}
                selected={selected?.inn === company.inn && selected?.name === company.name}
                onSelect={setSelected}
              />
            ))}
          </Stack>
        </div>
      )}

      <div style={{ borderTop: '1px solid #e9ecef', paddingTop: 8, flexShrink: 0 }}>
        <Group justify='flex-end'>
          <Button variant='default' size='xs' onClick={close}>
            Отмена
          </Button>
          <Button size='xs' disabled={!selected} onClick={handleAdd}>
            Добавить
          </Button>
        </Group>
      </div>
    </Stack>
  );
}
